<section class="pbw-panel">
  <h2 class="pbs-title">Qué le pasa a cada arrendamiento</h2>
  <p class="pbs-lead">
    El cálculo corre el mismo motor que la aplicación y aborta la transacción: nada se escribe. Tarda unos segundos por
    pago, así que va de uno en uno y lo puedes cancelar.
  </p>

  <!-- El avance real. No un spinner: quince segundos de spinner parecen una pantalla colgada. -->
  <div class="pbi-run" *ngIf="corriendo">
    <div class="pbi-run-head">
      <span><i class="fa-solid fa-spinner fa-spin"></i> Calculando {{ hechos }} de {{ total }}…</span>
      <button pButton type="button" label="Cancelar" class="p-button-sm p-button-text p-button-secondary" [disabled]="cancelando" (click)="cancelar()"></button>
    </div>
    <p-progressBar [value]="progreso"></p-progressBar>
    <span class="pbs-hint" *ngIf="cancelando">Se detiene al terminar el que está en curso.</span>
  </div>

  <ng-container *ngIf="calculados.length || conError.length">
    <div class="pbs-summary">
      <div class="pbs-stat is-ok">
        <div class="pbs-stat-v">{{ calculados.length }}</div>
        <div class="pbs-stat-l">Con impacto calculado</div>
      </div>
      <div class="pbs-stat">
        <div class="pbs-stat-v">{{ totalPlan | currency: 'MXN' : 'symbol-narrow' : '1.2-2' }}</div>
        <div class="pbs-stat-l">Se aplicaría en total</div>
      </div>
      <div class="pbs-stat is-warn" *ngIf="liquidaciones.length">
        <div class="pbs-stat-v">{{ liquidaciones.length }}</div>
        <div class="pbs-stat-l">Arrendamientos que se liquidan</div>
      </div>
      <div class="pbs-stat is-danger" *ngIf="conError.length">
        <div class="pbs-stat-v">{{ conError.length }}</div>
        <div class="pbs-stat-l">No se pudieron calcular</div>
      </div>
    </div>

    <!-- D1: la liquidación se advierte y se confirma una por una. Es una decisión, no un aviso. -->
    <div class="pbs-notice" *ngIf="bloqueantesSinConfirmar">
      <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
      <div>
        <strong>{{ bloqueantesSinConfirmar }} pago(s) tienen algo que confirmar</strong>
        Marca cada aviso para poder continuar. Son cosas que cambian de forma el arrendamiento, como dejarlo liquidado.
      </div>
    </div>

    <!-- El lote, en una frase. Es lo que el operador necesita leer para decidir si sigue; el detalle
         pago por pago solo hace falta donde algo pide una decisión. -->
    <p class="pbi-frase" *ngIf="calculados.length">
      Se aplicarían <b>{{ totalPlan | currency: 'MXN' : 'symbol-narrow' : '1.2-2' }}</b>
      en <b>{{ contratosAlcanzados }}</b> arrendamiento(s), con <b>{{ calculados.length }}</b> pago(s).
      <ng-container *ngIf="!destacados.length">Ninguno pide confirmación.</ng-container>
    </p>

    <ul class="pbi-list">
      <li *ngFor="let r of visibles" class="pbi-item" [class.is-error]="r.previewError">
        <div class="pbi-row">
          <div class="pbi-who">
            <strong>{{ r.clientName }}</strong>
            <span class="pbs-hint">{{ r.trackingKey }} · {{ r.amount | currency: 'MXN' : 'symbol-narrow' : '1.2-2' }}</span>
          </div>

          <div class="pbi-plan" *ngIf="r.preview">
            <span class="pbi-applied">{{ aplicado(r) | currency: 'MXN' : 'symbol-narrow' : '1.2-2' }}</span>
            <span class="pbs-hint">{{ (r.preview.plan || []).length }} letra(s)</span>
          </div>

          <div class="pbi-plan" *ngIf="!r.preview && !r.previewError">
            <span class="pbs-hint">Sin calcular</span>
          </div>

          <!-- El fallo, explicado con el mismo diccionario que Exigibilidad: qué pasó y cómo sigo.
               Antes salía el mensaje pelado del servidor. -->
          <div class="pbi-plan pbi-fail" *ngIf="explicado(r) as err">
            <span class="pbi-fail-t">{{ err.titulo }}</span>
            <span class="pbi-fail-q">{{ err.que }}</span>
            <span class="pbi-fail-n"><i class="fa-solid fa-arrow-right-long"></i> {{ err.comoSigo }}</span>
            <button pButton type="button" label="Reintentar" class="p-button-sm p-button-text" (click)="recalcular(r)"></button>
          </div>
        </div>

        <!--
          Las advertencias bloqueantes de este pago. Sin marcarlas no se aplica.

          El `<label>` va AL LADO del `p-checkbox` y lo apunta con `for`, nunca envolviéndolo. Envuelto,
          un clic sobre la casilla se contaba DOS veces —el componente de PrimeNG maneja el suyo y el
          `<label>` reenvía otro al `input` de dentro—, así que se marcaba y se desmarcaba en el mismo
          clic y la pantalla parecía muerta. El id se compone con el pago y el código del aviso porque
          esto vive dentro de dos `*ngFor`: uno fijo los repetiría en todas las filas y el `for`
          apuntaría siempre a la primera.
        -->
        <div class="pbi-warns" *ngIf="bloqueantes(r).length">
          <div class="pbi-warn" *ngFor="let w of bloqueantes(r)">
            <p-checkbox [(ngModel)]="r.confirmations[w.code]" [binary]="true" [inputId]="idAviso(r, w)"></p-checkbox>
            <label [for]="idAviso(r, w)">
              <strong>{{ etiqueta(w.code) }}</strong>
              <em *ngIf="w.message">{{ w.message }}</em>
            </label>
          </div>
        </div>
      </li>
    </ul>

    <!-- Los que no piden nada quedan plegados. No escondidos: se dice cuántos son y se abren de un
         clic. Esconder sin decirlo es lo que hace que nadie confíe en un resumen. -->
    <button type="button" class="pbi-mas" *ngIf="rutinarios.length" (click)="verTodos = !verTodos">
      <i class="fa-solid" [ngClass]="verTodos ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      {{ verTodos ? 'Ocultar' : 'Ver' }} los {{ rutinarios.length }} pago(s) que no piden nada
    </button>
  </ng-container>

  <div class="pbs-actions">
    <!-- Destinos puede no existir en este lote (nada que decidir): entonces atrás es el registro, y
         un botón que dice «Volver a los destinos» llevaría a otra pantalla. -->
    <button pButton type="button" label="Salir" class="p-button-text p-button-secondary pbs-left" [disabled]="corriendo" (click)="onExit.emit()"></button>
    <button pButton type="button" [label]="state.needsDestinations ? 'Volver a los destinos' : 'Volver al registro'"
      icon="fa-solid fa-chevron-left" class="p-button-outlined" [disabled]="corriendo" (click)="onBack.emit()"></button>
    <!-- Solo cuando ya se calculó algo: en el estado inicial el botón grande de arriba dice lo mismo,
         y dos botones que hacen lo mismo en la misma pantalla se leen como dos cosas distintas. -->
    <button pButton type="button" label="Calcular lo que falta" icon="fa-solid fa-arrows-rotate" class="p-button-outlined" *ngIf="!corriendo && (calculados.length || conError.length) && calculados.length < total" (click)="calcular()"></button>
    <button pButton type="button" label="Aplicar los pagos" icon="fa-solid fa-chevron-right" iconPos="right" [disabled]="!puedeSeguir" (click)="onDone.emit()"></button>
  </div>
</section>
